/**
 * Re-expresses a layout saved before the +Z-front frame (version ≤ 2) in it; α (frozen) is each model's old quarter turn.
 *
 *   world turn W = +90° about Y:  (x, y, z) → (z, y, −x),  world yaw ψ → ψ + 90° − α
 *   parent-local data turns by the parent's α: offsets R(α_p)·v, side labels +α_p
 *   a component's local yaw r → r + α_p − α_child
 */
import ALPHA from './orientationAlpha.json' with { type: 'json' };
import { getActiveAttachmentPoints, wrapAngle } from './geometry.js';
import { decodeFurniture } from './layoutCodec.js';
import { AttachmentPoint } from '../furniture/AttachmentPoint.js';

/** Layout version written by the +Z-front frame; older versions are migrated on read. */
export const FRAME_VERSION = '3';

const DEG = Math.PI / 180;
const CARDINAL = ['front', 'right', 'back', 'left'];
// As LayoutSerializer.findAttachmentPointByOffset.
const AP_OFFSET_MATCH_EPS = 0.005;

export const ORIENTATION_ALPHA = ALPHA.alpha;

/** Quarter turn (degrees) baked into a model's file by the migration. */
function alphaOf(urlCode) {
  const a = ORIENTATION_ALPHA[urlCode];
  return Number.isFinite(a) ? a : 0;
}

export function needsFrameMigration(version) {
  return !(Number(version) >= Number(FRAME_VERSION));
}

/** R(α)·v for a quarter turn, by exact swaps so a 5 mm offset match never drifts. */
export function turnVec(v, alphaDeg) {
  if (!v) return v;
  const { x, y, z } = v;
  switch (((alphaDeg % 360) + 360) % 360) {
    case 90: return { x: z, y, z: -x };
    case 270: return { x: -z, y, z: x };
    case 180: return { x: -x, y, z: -z };
    default: return { x, y, z };
  }
}

export function turnSide(side, alphaDeg) {
  const k = CARDINAL.indexOf(side);
  if (k === -1) return side;
  return CARDINAL[(k + Math.round(alphaDeg / 90) + 16) % 4];
}

/** W applied to a world point. */
export const worldPoint = (v) => turnVec(v, 90);

function findAp(parentData, parentVariant, side, offset, componentType) {
  const candidates = getActiveAttachmentPoints(parentData, parentVariant)
    .filter((ap) => ap.side === side && (ap.accepts ?? []).includes(componentType));
  if (offset) {
    const hit = candidates.find((ap) => ['x', 'y', 'z'].every((k) => Math.abs((ap.offset?.[k] ?? 0) - offset[k]) < AP_OFFSET_MATCH_EPS));
    if (hit) return hit;
  }
  return candidates[0] ?? null;
}

/**
 * @param {Array<Object>} items verbose layout items (decodeFurniture output) in the OLD frame
 * @param {(item: Object) => Object|null} lookup catalog entry for an item (findCatalogItem)
 * @returns {Array<Object>} new items in the current frame; the input is not mutated
 */
export function migrateLayoutItems(items, lookup) {
  const byId = new Map(items.map((it) => [String(it.id), it]));
  const catalog = new Map(items.map((it) => [it, lookup(it)]));
  const typeOf = (it) => catalog.get(it)?.componentType ?? it.componentType;

  return items.map((item) => {
    const out = { ...item };
    const alphaC = alphaOf(item.urlCode);
    const parent = item.attachedTo != null ? byId.get(String(item.attachedTo)) : null;
    const local = parent && !(typeOf(item) === 'module' && typeOf(parent) === 'module');

    if (!local) {
      // World pose: roots and module→module links.
      out.position = worldPoint(item.position);
      out.rotation = wrapAngle((item.rotation ?? 0) + (90 - alphaC) * DEG);
    }

    if (parent) {
      const alphaP = alphaOf(parent.urlCode);
      out.attachedSide = turnSide(item.attachedSide, alphaP);
      out.attachedPointOffset = turnVec(item.attachedPointOffset, alphaP);
      out.attachmentLocalPosition = turnVec(item.attachmentLocalPosition, alphaP);
      if (item.moduleConnectionSide) out.moduleConnectionSide = turnSide(item.moduleConnectionSide, alphaC);

      if (local) {
        out.position = turnVec(item.position, alphaP);
        if (item.attachedSide !== 'inherit') {
          // The loader reads a yaw of ~0 as "the AP's rest yaw": resolve that sentinel both ways.
          const parentData = catalog.get(parent);
          const ap = parentData && findAp(parentData, parent.variantIndex ?? 0, out.attachedSide, out.attachedPointOffset, typeOf(item));
          const restNew = ap ? AttachmentPoint.restRotationY(ap) : null;
          let oldYaw = item.rotation ?? 0;
          if (Math.abs(oldYaw) <= 1e-10 && restNew !== null) oldYaw = restNew - (alphaP + 90) * DEG;
          const next = wrapAngle(oldYaw + (alphaP - alphaC) * DEG);
          const sameAsRest = restNew !== null && Math.abs(wrapAngle(next - restNew)) < 1e-6;
          // 2π: the identity turn, but not the sentinel.
          out.rotation = Math.abs(next) > 1e-10 || sameAsRest ? next : 2 * Math.PI;
        }
      }
    }
    return out;
  });
}

/**
 * A layout's verbose furniture and camera in the current frame, whatever version wrote it.
 * @param {Object} state picked layout ({version, furniture, camera, …})
 * @param {(item: Object) => Object|null} lookup catalog entry for an item
 */
export function readLayout(state, lookup) {
  const items = decodeFurniture(state.furniture || [], state.version);
  if (!needsFrameMigration(state.version)) return { items, camera: state.camera };
  return { items: migrateLayoutItems(items, lookup), camera: migrateCamera(state.camera) };
}

/** W applied to a saved camera ({position, target, sceneMode}). */
export function migrateCamera(camera) {
  if (!camera) return camera;
  return {
    ...camera,
    ...(camera.position && { position: worldPoint(camera.position) }),
    ...(camera.target && { target: worldPoint(camera.target) }),
  };
}
